import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { useTimeOfDay } from '../hooks/useTimeOfDay'
import heroBg from '../assets/hero-bg.webp'

const heroOverlay = {
  morning: 'from-espresso/50 via-espresso/30 to-espresso/60',
  afternoon: 'from-espresso/70 via-espresso/50 to-espresso/80',
  night: 'from-espresso/85 via-espresso/70 to-espresso/90',
} as const

export default function Hero() {
  const { t } = useTranslation()
  const timeOfDay = useTimeOfDay()
  const marqueeText = t('hero.marquee')

  return (
    <section
      className="relative min-h-screen flex flex-col justify-center items-center overflow-hidden pt-28 md:pt-0"
      style={{ cursor: "url('/assets/cursor-heart-sand.png') 16 16, auto" }}
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src={heroBg}
          alt="buenas studio – Studio für Design, Text und Software"
          width={1200}
          height={1483}
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover object-[center_35%]"
        />
        <div
          className={`absolute inset-0 bg-gradient-to-b ${heroOverlay[timeOfDay]} transition-colors duration-[2000ms]`}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-6 pb-16 md:pb-24">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.05 }}
          className="font-body text-sand text-xs tracking-[0.35em] uppercase mb-2 md:-mb-4"
        >
          {t('hero.kicker')}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
          className="font-headline italic text-sand text-6xl sm:text-8xl md:text-[11rem] font-bold leading-none mb-6"
        >
          buenas
        </motion.h1>
        <motion.p
          initial={{ y: 15 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="font-body text-sand/90 text-base sm:text-lg max-w-lg mx-auto mb-10"
        >
          {t('hero.tagline')}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a
            href="#contact"
            className="bg-rot text-sand rounded-full px-8 py-3 font-body font-semibold hover:bg-lachs transition-colors"
          >
            {t('hero.cta1')}
          </a>
          <a
            href="#services"
            className="border border-sand/70 text-sand rounded-full px-8 py-3 font-body hover:bg-sand/10 transition-colors"
          >
            {t('hero.cta2')}
          </a>
        </motion.div>
      </div>

      {/* Marquee */}
      <div className="absolute bottom-0 left-0 right-0 z-10 bg-rot/90 py-3 overflow-hidden">
        <div className="marquee-track">
          <span className="font-headline text-sand text-lg tracking-widest">
            {marqueeText.repeat(12)}
          </span>
          <span className="font-headline text-sand text-lg tracking-widest" aria-hidden>
            {marqueeText.repeat(12)}
          </span>
        </div>
      </div>
    </section>
  )
}
